/**
 * tutorApi — thin JSON client for Tutor::ConversationsController (TASK-010
 * Phase 2 CRUD half): list/create/show/delete conversations. Mirrors
 * quizApi.ts's style exactly (raw fetch + CSRF header, no axios) — this is
 * the established convention for non-Inertia JSON endpoints in this app.
 *
 * The streaming turn (`POST .../stream`) is deliberately NOT here — see
 * ./tutorStream.ts (different transport: SSE via
 * `@microsoft/fetch-event-source`, not a single JSON response).
 *
 * The wire types moved to `@arianna/reader-core/tutor` in TASK-019 Phase 4
 * ("Package condivisi — estrazione tutor") so mobile can reuse them
 * unchanged — re-exported below so no other file in this app has to change
 * its import.
 */

import { getCsrfToken } from './syncApi.js'
import type {
  TutorBlockContent,
  TutorConversationDetail,
  TutorConversationSummary,
  TutorFeedback,
  TutorFocusBlock,
} from '@arianna/reader-core/tutor'

export type {
  TutorBlockContent,
  TutorCitation,
  TutorConversationDetail,
  TutorConversationSummary,
  TutorFeedback,
  TutorFocusBlock,
  TutorMessage,
  TutorMessageRole,
  TutorToolData,
} from '@arianna/reader-core/tutor'

function jsonHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null
  if (!res.ok) {
    throw new Error(data?.error ?? `Richiesta tutor fallita: ${res.status}`)
  }
  return data as T
}

function conversationsPath(manualVersionId: number): string {
  return `/web/courses/${manualVersionId}/tutor/conversations`
}

export async function fetchTutorConversations(
  manualVersionId: number,
): Promise<TutorConversationSummary[]> {
  const res = await fetch(conversationsPath(manualVersionId), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  return parseJsonOrThrow<TutorConversationSummary[]>(res)
}

export async function createTutorConversation(
  manualVersionId: number,
  focusBlock?: TutorFocusBlock,
): Promise<TutorConversationSummary> {
  const init: RequestInit = {
    method: 'POST',
    headers: jsonHeaders(),
    credentials: 'same-origin',
  }
  if (focusBlock != null) {
    init.body = JSON.stringify({ focus_block: focusBlock })
  }
  const res = await fetch(conversationsPath(manualVersionId), init)
  return parseJsonOrThrow<TutorConversationSummary>(res)
}

export async function fetchTutorConversation(
  manualVersionId: number,
  conversationId: string,
): Promise<TutorConversationDetail> {
  const res = await fetch(`${conversationsPath(manualVersionId)}/${conversationId}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  return parseJsonOrThrow<TutorConversationDetail>(res)
}

export async function deleteTutorConversation(
  manualVersionId: number,
  conversationId: string,
): Promise<void> {
  const res = await fetch(`${conversationsPath(manualVersionId)}/${conversationId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': getCsrfToken() },
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null
    throw new Error(data?.error ?? `Impossibile eliminare la conversazione: ${res.status}`)
  }
}

/**
 * GET .../tutor/blocks/*block_id — the cited block's full text (TASK-012
 * REVISIONE 2 Parte C). `block_id` can legitimately contain "/" (e.g.
 * "1.1.1/b1") — the backend route is a glob segment for exactly this
 * reason, so this encodes EACH "/"-separated piece individually (never the
 * "/" itself) rather than a single `encodeURIComponent(blockId)` call, which
 * would turn every "/" into a literal "%2F" and break the route's glob
 * match.
 */
export async function fetchTutorBlockContent(
  manualVersionId: number,
  blockId: string,
): Promise<TutorBlockContent> {
  const encodedBlockId = blockId.split('/').map(encodeURIComponent).join('/')
  const res = await fetch(`/web/courses/${manualVersionId}/tutor/blocks/${encodedBlockId}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  return parseJsonOrThrow<TutorBlockContent>(res)
}

/**
 * PATCH .../conversations/:id/messages/:messageId/feedback — sets or clears
 * (feedback: null) a student's vote on an assistant message. Only callable
 * against a message with a REAL (server-persisted) id — see TutorOverlay.tsx
 * `TutorViewMessage.persisted`, which gates the feedback UI accordingly: a
 * just-streamed message only gets a real id after the post-turn conversation
 * refetch, since the `done` SSE event doesn't carry one.
 */
export async function setMessageFeedback(
  manualVersionId: number,
  conversationId: string,
  messageId: string,
  feedback: TutorFeedback,
): Promise<TutorFeedback> {
  const res = await fetch(
    `${conversationsPath(manualVersionId)}/${conversationId}/messages/${messageId}/feedback`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ feedback }),
    },
  )
  const data = await parseJsonOrThrow<{ feedback: TutorFeedback }>(res)
  return data.feedback
}
